
import { Navigation } from "@/components/Navigation";
import { Hero } from "@/components/landing/Hero";
import { Features } from "@/components/landing/Features";
import { Benefits } from "@/components/landing/Benefits";
import { Testimonials } from "@/components/landing/Testimonials";
import { Pricing } from "@/components/landing/Pricing";
import { Footer } from "@/components/landing/Footer";
import { useEffect } from "react";
import { useLanguage } from "@/components/LanguageContext";
import { SEOHead } from "@/components/SEOHead";
import { useLanguagePath } from "@/utils/language";
import { useMetaPixel } from "@/hooks/useMetaPixel";

const Index = () => {
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const { sendViewContentEvent } = useMetaPixel();

  // Schema.org data for the homepage
  const schemaData = {
    name: "TrainerFOLIO",
    description: language === 'pt' 
      ? "Aplicação tudo-em-um para Personal Trainers com IA e gestão ilimitada de alunos" 
      : language === 'es'
        ? "Aplicación todo-en-uno para Entrenadores Personales con IA y gestión ilimitada de estudiantes"
        : language === 'fr'
          ? "Application tout-en-un pour Personal Trainers avec IA et gestion illimitée d'élèves"
          : "All-in-one application for Personal Trainers with AI and unlimited student management",
    url: `https://trainerfolio.com${getPath('/')}`,
    logo: "/lovable-uploads/76235432-f790-4bd0-a843-a378bbe969d9.png",
    sameAs: [
      "https://instagram.com/trainerfolio",
      "https://facebook.com/trainerfolio",
      "https://twitter.com/trainerfolio"
    ]
  };

  // Send ViewContent event for homepage
  useEffect(() => {
    sendViewContentEvent('Homepage', 'landing_page', 0);
  }, [sendViewContentEvent]);

  const getTitle = () => {
    switch(language) {
      case 'pt':
        return "TrainerFOLIO | Aplicação com IA para Personal Trainers e Alunos Ilimitados";
      case 'es':
        return "TrainerFOLIO | Aplicación con IA para Entrenadores Personales y Estudiantes Ilimitados";
      case 'fr':
        return "TrainerFOLIO | Application avec IA pour Personal Trainers et Élèves Illimités";
      default:
        return "TrainerFOLIO | AI-Powered App for Personal Trainers with Unlimited Students";
    }
  };

  const getDescription = () => {
    switch(language) {
      case 'pt':
        return "Aplicação tudo-em-um para Personal Trainers com recursos de IA e gestão ilimitada de alunos. Planeie treinos, acompanhe a nutrição, avalie o progresso e desenvolva o seu negócio.";
      case 'es':
        return "Aplicación todo-en-uno para Entrenadores Personales con funciones de IA y gestión ilimitada de estudiantes. Planifica entrenamientos, sigue la nutrición, evalúa el progreso y haz crecer tu negocio.";
      case 'fr':
        return "Application tout-en-un pour Personal Trainers avec fonctionnalités IA et gestion illimitée d'élèves. Planifiez des entraînements, suivez la nutrition, évaluez les progrès et développez votre entreprise.";
      default:
        return "All-in-one application for Personal Trainers with AI features and unlimited student management. Plan workouts, track nutrition, assess progress, and grow your business.";
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead 
        title={getTitle()}
        description={getDescription()}
        canonicalPath={getPath('/')}
        schemaType="WebSite"
        schemaData={schemaData}
      />
      
      <header>
        <Navigation />
      </header>
      
      <main className="flex-grow bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950">
        <section id="hero" aria-labelledby="hero-heading">
          <h1 id="hero-heading" className="sr-only">{getTitle()}</h1>
          <Hero />
        </section>
        
        <section id="features" aria-labelledby="features-heading">
          <h2 id="features-heading" className="sr-only">
            {language === 'pt' ? 'Funcionalidades' : language === 'es' ? 'Características' : language === 'fr' ? 'Fonctionnalités' : 'Features'}
          </h2>
          <Features />
        </section>
        
        <section id="benefits" aria-labelledby="benefits-heading">
          <h2 id="benefits-heading" className="sr-only">
            {language === 'pt' ? 'Vantagens' : language === 'es' ? 'Beneficios' : language === 'fr' ? 'Avantages' : 'Benefits'}
          </h2>
          <Benefits />
        </section>
        
        <section id="testimonials" aria-labelledby="testimonials-heading">
          <h2 id="testimonials-heading" className="sr-only">
            {language === 'pt' ? 'Depoimentos' : language === 'es' ? 'Testimonios' : language === 'fr' ? 'Témoignages' : 'Testimonials'}
          </h2>
          <Testimonials />
        </section>
        
        <section id="pricing" aria-labelledby="pricing-heading">
          <h2 id="pricing-heading" className="sr-only">
            {language === 'pt' ? 'Planos' : language === 'es' ? 'Precios' : language === 'fr' ? 'Forfaits' : 'Pricing'}
          </h2>
          <Pricing />
        </section>
      </main>
      
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default Index;
